import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MessageCircle, Loader2, AlertTriangle, Target,
  Plus, Building2, Calendar, TrendingUp, ClipboardList, Sparkles,
  Upload, FileText, DollarSign, Clock, Flame, ArrowRight, CheckCircle2,
  ShieldCheck, Briefcase
} from "lucide-react";
import { toast } from "sonner";
import { useCustomerAuth } from "../../contexts/CustomerAuthContext";
import { useDocMeta } from "../../lib/useDocMeta";
import { KV, Bullets, RiskBadge } from "../../components/report/Primitives";
import Scorecard from "../../components/report/Scorecard";
import { extractDocument } from "../../lib/api";
import RequestProposalServicesModal from "./RequestProposalServicesModal";

// -------- Derived status helpers (no DB schema change) --------
// Story #1 rule: derive pursuit status client-side from existing fields.
//   report/final verdict present    -> "Analyzed"
//   status === "failed" | "error"   -> "Needs Attention"
//   status === "processing" (or missing report) -> "Processing"
//   otherwise                       -> use raw status uppercased
function derivePursuitStatus(p) {
  const raw = (p.status || "").toLowerCase();
  if (raw === "failed" || raw === "error") return "Needs Attention";
  const hasReportSignal =
    p.final_verdict || (p.overall_score !== null && p.overall_score !== undefined) || p.opportunity_name;
  if (hasReportSignal) return "Analyzed";
  if (raw === "processing" || raw === "queued" || raw === "pending" || !raw) return "Processing";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function statusStyle(status) {
  switch (status) {
    case "Analyzed":        return { dot: "bg-emerald-400", text: "text-emerald-300", border: "border-emerald-500/40", bg: "bg-emerald-500/5" };
    case "Processing":      return { dot: "bg-amber-400 animate-pulse", text: "text-amber-300", border: "border-amber-500/40", bg: "bg-amber-500/5" };
    case "Needs Attention": return { dot: "bg-rose-400", text: "text-rose-300", border: "border-rose-500/40", bg: "bg-rose-500/5" };
    default:                return { dot: "bg-slate-400", text: "text-slate-300", border: "border-slate-500/40", bg: "bg-slate-500/5" };
  }
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return "—"; }
}

// -------- Story #2 helpers: Recommendation, Time Remaining, Priority --------
// Recommendation canonical values (backend post-processor normalizes to these):
//   GO · GO WITH CAUTION · NO GO · INSUFFICIENT DATA
function deriveRecommendation(p) {
  const v = (p.final_verdict || "").trim().toUpperCase();
  if (!v) return "INSUFFICIENT DATA";
  // Backend post-processor already returns canonical values — trust them first.
  if (v === "GO" || v === "GO WITH CAUTION" || v === "NO GO" || v === "INSUFFICIENT DATA") return v;
  // Legacy fallback for pre-Story-#3 analyses:
  if (v.includes("NO GO") || v === "NO-GO" || v === "NO_GO" || v.includes("NO BID") || v.includes("DO NOT PURSUE")) return "NO GO";
  if (v.includes("CAUTION")) return "GO WITH CAUTION";
  if (v === "GO" || v.startsWith("GO ") || v === "PURSUE" || v.includes("STRONG BID") || v === "BID") return "GO";
  return "INSUFFICIENT DATA";
}

// Analysis Confidence — pass-through helper. Normalizes to null when missing.
function analysisConfidence(p) {
  const c = p?.analysis_confidence;
  if (c === null || c === undefined) return null;
  const n = Number(c);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : null;
}

function confidenceStyle(conf) {
  if (conf === null || conf === undefined) return { text: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/40" };
  if (conf >= 80) return { text: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/40" };
  if (conf >= 60) return { text: "text-sky-300",     bg: "bg-sky-500/10",     border: "border-sky-500/40" };
  if (conf >= 40) return { text: "text-amber-300",   bg: "bg-amber-500/10",   border: "border-amber-500/40" };
  return              { text: "text-rose-300",    bg: "bg-rose-500/10",    border: "border-rose-500/40" };
}


function recommendationStyle(rec) {
  switch (rec) {
    case "GO":               return { text: "text-emerald-300", border: "border-emerald-500/50", bg: "bg-emerald-500/10", dot: "bg-emerald-400" };
    case "GO WITH CAUTION":  return { text: "text-amber-300",   border: "border-amber-500/50",   bg: "bg-amber-500/10",   dot: "bg-amber-400" };
    case "NO GO":            return { text: "text-rose-300",    border: "border-rose-500/50",    bg: "bg-rose-500/10",    dot: "bg-rose-400" };
    default:                 return { text: "text-slate-300",   border: "border-slate-500/40",   bg: "bg-slate-500/10",   dot: "bg-slate-400" };
  }
}

// Story #2: parse ISO or common "YYYY-MM-DD" due dates. Returns { days, label, tone }.
//   overdue        -> Overdue (rose)
//   0              -> Due Today (rose)
//   1..3           -> "N day(s) left" (rose)
//   4..14          -> "N days left" (amber)
//   >14            -> "N days left" (slate)
//   no due date    -> null
function computeTimeRemaining(dueIso) {
  if (!dueIso) return null;
  const due = new Date(dueIso);
  if (Number.isNaN(due.getTime())) return null;
  const startOfDay = (d) => { const c = new Date(d); c.setHours(0,0,0,0); return c; };
  const today = startOfDay(new Date());
  const target = startOfDay(due);
  const days = Math.round((target.getTime() - today.getTime()) / 86400000);
  if (days < 0) return { days, label: "Overdue", tone: "rose" };
  if (days === 0) return { days, label: "Due Today", tone: "rose" };
  if (days <= 3) return { days, label: `${days} day${days === 1 ? "" : "s"} left`, tone: "rose" };
  if (days <= 14) return { days, label: `${days} days left`, tone: "amber" };
  return { days, label: `${days} days left`, tone: "slate" };
}

// Parse ceiling strings like "$10M", "10,000,000", "5.5M", "$10 million" into
// a rough USD number for the priority calculator. Never displayed — used only
// for scoring.
function parseCeilingUsd(ceiling) {
  if (ceiling === null || ceiling === undefined) return null;
  let s = String(ceiling).replace(/[$,]/g, "").trim().toUpperCase();
  if (!s) return null;
  // Normalize spelled-out magnitudes to single-letter suffix.
  s = s.replace(/\bTRILLION\b/g, "T")
       .replace(/\bBILLION\b/g, "B")
       .replace(/\bMILLION\b/g, "M")
       .replace(/\bTHOUSAND\b/g, "K")
       .replace(/\s+/g, "");
  const m = s.match(/^(\d+(?:\.\d+)?)([KMBT])?$/);
  if (!m) {
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
  const mult = { K: 1e3, M: 1e6, B: 1e9, T: 1e12 }[m[2] || ""] || 1;
  return Number(m[1]) * mult;
}

// Priority derivation — deterministic, no AI. Combines Recommendation + Time
// Remaining + Ceiling into HIGH / MEDIUM / LOW.
//   HIGH:   GO or GO WITH CAUTION AND (overdue|<=14 days) AND ceiling >= $1M
//   HIGH:   any status + ceiling >= $10M + within 30 days
//   MEDIUM: GO / GO WITH CAUTION, or ceiling >= $1M, or <=14 days remaining
//   LOW:    everything else (incl. NO GO, INSUFFICIENT DATA with no urgency)
function derivePriority(p) {
  const rec = deriveRecommendation(p);
  const tr = computeTimeRemaining(p.response_due_date);
  const usd = parseCeilingUsd(p.estimated_ceiling);
  const urgent = tr && (tr.days <= 14);
  const highCeiling = usd !== null && usd >= 1_000_000;
  const megaCeiling = usd !== null && usd >= 10_000_000;
  const positive = rec === "GO" || rec === "GO WITH CAUTION";
  if (positive && urgent && highCeiling) return "HIGH";
  if (megaCeiling && tr && tr.days <= 30) return "HIGH";
  if (positive || highCeiling || urgent) return "MEDIUM";
  return "LOW";
}

function priorityStyle(pri) {
  switch (pri) {
    case "HIGH":   return { text: "text-rose-300",   bg: "bg-rose-500/10",   border: "border-rose-500/50",   icon: Flame };
    case "MEDIUM": return { text: "text-amber-300",  bg: "bg-amber-500/10",  border: "border-amber-500/50",  icon: TrendingUp };
    default:       return { text: "text-slate-300",  bg: "bg-slate-500/10",  border: "border-slate-500/40",  icon: TrendingUp };
  }
}

// Format a ceiling like "28500000" → "$28,500,000" while leaving
// pre-formatted values ("$10M", "$5.5M", "Ceiling TBD") untouched.
function formatCeilingDisplay(v) {
  if (v === null || v === undefined || v === "" || v === "—") return null;
  const s = String(v).trim();
  if (/[a-zA-Z$]/.test(s)) return s;
  const n = Number(s.replace(/,/g, ""));
  if (Number.isFinite(n)) return `$${n.toLocaleString("en-US")}`;
  return s;
}

function timeToneStyle(tone) {
  switch (tone) {
    case "rose":  return { text: "text-rose-300",  bg: "bg-rose-500/10",  border: "border-rose-500/40" };
    case "amber": return { text: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/40" };
    default:      return { text: "text-slate-300", bg: "bg-slate-500/10", border: "border-slate-500/40" };
  }
}

// -------- Pursuits List (Story #1 — opportunity-centric framing) --------
export function CustomerPursuits() {
  useDocMeta({ title: "Pursuits · GovBidIQ", path: "/app/pursuits" });
  const { custApi } = useCustomerAuth();
  const [items, setItems] = useState(null);

  useEffect(() => {
    custApi
      .get("/customer/reports")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, [custApi]);

  const stats = useMemo(() => {
    const list = items || [];
    return {
      total: list.length,
      analyzed: list.filter((p) => derivePursuitStatus(p) === "Analyzed").length,
      processing: list.filter((p) => derivePursuitStatus(p) === "Processing").length,
      attention: list.filter((p) => derivePursuitStatus(p) === "Needs Attention").length,
    };
  }, [items]);

  if (items === null) {
    return (
      <div className="flex items-center gap-2 text-slate-500" data-testid="cust-pursuits-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> <span className="font-mono text-[11px] tracking-widest uppercase">Loading pursuits…</span>
      </div>
    );
  }

  return (
    <div data-testid="cust-pursuits">
      <div className="tick-label text-sky-400 mb-1">/ Pursuits</div>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display font-black text-3xl text-slate-100">Your pursuits</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-xl">
            Every opportunity you upload becomes a pursuit — tracked from intake through analysis to Go/No-Go.
          </p>
        </div>
        <Link
          to="/app/pursuits/new"
          data-testid="cust-start-new-pursuit"
          className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Start New Pursuit
        </Link>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-4 gap-px bg-[var(--border-soft)] mb-6" data-testid="cust-pursuits-stats">
        <StatCell testid="cust-pursuits-stat-total"      label="Total"          value={stats.total}      icon={ClipboardList} accent="sky" />
        <StatCell testid="cust-pursuits-stat-analyzed"   label="Analyzed"       value={stats.analyzed}   icon={Target}        accent="emerald" />
        <StatCell testid="cust-pursuits-stat-processing" label="Processing"     value={stats.processing} icon={Sparkles}      accent="amber" />
        <StatCell testid="cust-pursuits-stat-attention"  label="Needs Attention" value={stats.attention} icon={AlertTriangle} accent="rose" />
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--border-soft)] bg-[var(--bg-secondary)] p-10 text-center" data-testid="cust-pursuits-empty">
          <Target className="w-8 h-8 text-slate-600 mx-auto mb-3" strokeWidth={1.4} />
          <div className="font-display font-bold text-slate-100 text-lg mb-1">No pursuits yet</div>
          <div className="text-slate-500 text-sm mb-5">Upload your first federal opportunity to start tracking it as a pursuit.</div>
          <Link
            to="/app/pursuits/new"
            data-testid="cust-pursuits-empty-cta"
            className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Start New Pursuit
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="cust-pursuits-grid">
          {items.map((p) => (
            <PursuitCard key={p.id} pursuit={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value, icon: Icon, accent, testid }) {
  const color = accent === "emerald" ? "text-emerald-400"
              : accent === "amber" ? "text-amber-400"
              : accent === "rose" ? "text-rose-400"
              : "text-sky-400";
  return (
    <div className="bg-[var(--bg-secondary)] p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="tick-label">{label}</div>
        <Icon className={`w-3.5 h-3.5 ${color}`} strokeWidth={1.6} />
      </div>
      <div className={`metric-number text-3xl leading-none ${color}`}>{value}</div>
    </div>
  );
}

function PursuitCard({ pursuit }) {
  const status = derivePursuitStatus(pursuit);
  const s = statusStyle(status);
  const rec = deriveRecommendation(pursuit);
  const recS = recommendationStyle(rec);
  const priority = derivePriority(pursuit);
  const priS = priorityStyle(priority);
  const PriIcon = priS.icon;
  const timeRem = computeTimeRemaining(pursuit.response_due_date);
  const timeS = timeRem ? timeToneStyle(timeRem.tone) : null;
  const title = pursuit.opportunity_name || pursuit.document_name || pursuit.id;
  const agency = pursuit.agency || "Agency TBD";
  const ceiling = formatCeilingDisplay(pursuit.estimated_ceiling);

  return (
    <Link
      to={`/app/pursuits/${pursuit.id}`}
      data-testid={`cust-pursuit-card-${pursuit.id}`}
      className="group bg-[var(--bg-secondary)] border border-[var(--border-soft)] hover:border-sky-500/60 transition-colors p-5 flex flex-col gap-4"
    >
      {/* Header row: status + priority + tier */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 border ${s.border} ${s.bg}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            <span className={`font-mono text-[9px] tracking-[0.2em] uppercase ${s.text}`} data-testid={`cust-pursuit-status-${pursuit.id}`}>{status}</span>
          </div>
          <div
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 border ${priS.border} ${priS.bg}`}
            data-testid={`cust-pursuit-priority-${pursuit.id}`}
            title={`Priority: ${priority}`}
          >
            <PriIcon className={`w-2.5 h-2.5 ${priS.text}`} strokeWidth={2.2} />
            <span className={`font-mono text-[9px] tracking-[0.2em] uppercase ${priS.text}`}>{priority}</span>
          </div>
        </div>
        {pursuit.tier && (
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-sky-400 border border-sky-500/40 bg-sky-500/5 px-2 py-0.5">
            Tier · {pursuit.tier}
          </span>
        )}
      </div>

      {/* Title + solicitation number */}
      <div>
        <div className="font-display font-bold text-slate-100 leading-tight line-clamp-2 group-hover:text-sky-300 transition-colors" data-testid={`cust-pursuit-title-${pursuit.id}`}>
          {title}
        </div>
        {pursuit.solicitation_number && (
          <div className="font-mono text-[10px] text-slate-500 mt-1 truncate">Sol # {pursuit.solicitation_number}</div>
        )}
      </div>

      {/* Meta grid: agency + ceiling + due + created */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-slate-400 flex-1">
        <div className="flex items-center gap-2 col-span-2">
          <Building2 className="w-3 h-3 text-slate-500 flex-shrink-0" />
          <span className="truncate" data-testid={`cust-pursuit-agency-${pursuit.id}`}>{agency}</span>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-3 h-3 text-slate-500 flex-shrink-0" />
          <span className="truncate" data-testid={`cust-pursuit-ceiling-${pursuit.id}`}>{ceiling || "Ceiling TBD"}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-3 h-3 text-slate-500 flex-shrink-0" />
          <span className="truncate" data-testid={`cust-pursuit-due-${pursuit.id}`}>
            {pursuit.response_due_date ? `Due ${formatDate(pursuit.response_due_date)}` : "Due date TBD"}
          </span>
        </div>
        <div className="flex items-center gap-2 col-span-2">
          <Clock className="w-3 h-3 text-slate-500 flex-shrink-0" />
          <span className="text-slate-500 text-[10px] font-mono tracking-widest uppercase" data-testid={`cust-pursuit-created-${pursuit.id}`}>
            Created {formatDate(pursuit.created_at)}
          </span>
        </div>
      </div>

      {/* Time remaining pill (only when we have a due date) */}
      {timeRem && (
        <div className={`inline-flex items-center gap-1.5 px-2 py-1 border ${timeS.border} ${timeS.bg} self-start`} data-testid={`cust-pursuit-time-remaining-${pursuit.id}`}>
          <Clock className={`w-3 h-3 ${timeS.text}`} />
          <span className={`font-mono text-[10px] tracking-[0.15em] uppercase ${timeS.text}`}>{timeRem.label}</span>
        </div>
      )}

      {/* Footer: bid recommendation + analysis confidence + overall score */}
      <div className="flex items-center justify-between pt-3 border-t border-[var(--border-soft)] gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 border ${recS.border} ${recS.bg}`}
            data-testid={`cust-pursuit-recommendation-${pursuit.id}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${recS.dot}`} />
            <span className={`font-mono text-[10px] tracking-[0.2em] uppercase font-bold ${recS.text}`}>{rec}</span>
          </div>
          {(() => {
            const conf = analysisConfidence(pursuit);
            if (conf === null) return null;
            const cS = confidenceStyle(conf);
            return (
              <div
                className={`inline-flex items-center gap-1 px-2 py-1 border ${cS.border} ${cS.bg}`}
                data-testid={`cust-pursuit-confidence-${pursuit.id}`}
                title="Analysis Confidence — confidence in the analysis given available information. Not a probability of contract award."
              >
                <span className={`font-mono text-[9px] tracking-[0.15em] uppercase ${cS.text}`}>Confidence</span>
                <span className={`font-mono text-[11px] font-bold ${cS.text}`}>{conf}%</span>
              </div>
            );
          })()}
        </div>
        {(pursuit.overall_score !== null && pursuit.overall_score !== undefined) && (
          <div className="flex items-baseline gap-1" data-testid={`cust-pursuit-score-${pursuit.id}`}>
            <TrendingUp className="w-3 h-3 text-sky-400" />
            <span className="metric-number text-lg text-sky-400 leading-none">{pursuit.overall_score}</span>
            <span className="font-mono text-[10px] text-slate-500">/100</span>
          </div>
        )}
      </div>
    </Link>
  );
}

// -------- Story #2 — Opportunity Intake (real upload flow) --------
// 3-step intake:
//   1) Upload the solicitation (PDF/DOCX/TXT) -> /api/extract
//   2) Confirm minimum manual fields (agency, opportunity_name, ceiling, due date)
//   3) Confirm contractor profile (loaded from /api/customer/profile, editable)
// Submit -> POST /api/customer/analyses -> navigate to /app/pursuits/{id}
const INTAKE_PROFILE_GROUPS = [
  {
    label: "Company Identity",
    icon: Building2,
    fields: [
      { name: "company_name",         label: "Company Name",         placeholder: "Acme Federal Solutions LLC", required: true },
      { name: "years_in_business",    label: "Years in Business",    placeholder: "12" },
      { name: "annual_revenue",       label: "Annual Revenue",       placeholder: "$24M" },
      { name: "number_of_employees",  label: "Number of Employees",  placeholder: "85" },
      { name: "geographic_coverage",  label: "Geographic Coverage",  placeholder: "CONUS / OCONUS / DMV" },
    ],
  },
  {
    label: "Classification & Set-Asides",
    icon: ShieldCheck,
    fields: [
      { name: "primary_naics",         label: "Primary NAICS",         placeholder: "541512" },
      { name: "secondary_naics",       label: "Secondary NAICS",       placeholder: "541330, 541611" },
      { name: "business_size",         label: "Business Size",         placeholder: "Small / Large" },
      { name: "small_business_status", label: "Small Business",        placeholder: "Yes / No" },
      { name: "sdvosb_status",         label: "SDVOSB",                placeholder: "Yes / No" },
      { name: "wosb_status",           label: "WOSB / EDWOSB",         placeholder: "Yes / No" },
      { name: "hubzone_status",        label: "HUBZone",               placeholder: "Yes / No" },
      { name: "eight_a_status",        label: "8(a) Status",           placeholder: "Active / Graduated / No" },
    ],
  },
  {
    label: "Capabilities & Past Performance",
    icon: Briefcase,
    fields: [
      { name: "core_capabilities",         label: "Core Capabilities",         placeholder: "Cyber, cloud migration, C5ISR, software dev...", type: "textarea" },
      { name: "relevant_past_performance", label: "Relevant Past Performance", placeholder: "List prior contracts, agencies, dollar values, roles...", type: "textarea" },
      { name: "existing_contract_vehicles",label: "Existing Contract Vehicles",placeholder: "GSA MAS, SEWP V, OASIS+, CIO-SP4...", type: "textarea" },
      { name: "security_clearances",       label: "Security Clearances",       placeholder: "Facility: TS/SCI. Personnel: 12 TS, 30 Secret", type: "textarea" },
      { name: "key_certifications",        label: "Key Certifications",        placeholder: "CMMC L2, ISO 27001, CMMI Dev 3, FedRAMP...", type: "textarea" },
      { name: "teaming_partners",          label: "Teaming Partners",          placeholder: "Names, roles, complementary capabilities...", type: "textarea" },
    ],
  },
];

const EMPTY_PROFILE = INTAKE_PROFILE_GROUPS.flatMap((g) => g.fields.map((f) => f.name))
  .reduce((acc, k) => ({ ...acc, [k]: "" }), {});

// Lightweight regex hints — pre-fill intake fields from the extracted text so
// customers only have to correct/confirm. No AI, no LLM call, no backend hop.
function sniffIntakeHints(text) {
  const hints = {};
  if (!text) return hints;
  // Solicitation / RFP number: common formats like "W91QVN-25-R-0001" or "N00189-25-Q-1234"
  const solMatch = text.match(/\b([A-Z0-9]{4,}[-][A-Z0-9]{1,}[-][A-Z][-][A-Z0-9]{3,})\b/);
  if (solMatch) hints.solicitation_number = solMatch[1];
  // Agency hint from common prefixes
  const agencyHints = [
    { pat: /department of (defense|the army|the navy|the air force|homeland security|veterans affairs|state|energy|justice|the interior|transportation|commerce|labor|agriculture|education|health and human services)/i, out: (m) => `Department of ${m[1]}` },
    { pat: /\bU\.?S\.?\s+(Army|Navy|Air Force|Marine Corps|Coast Guard|Space Force)\b/i, out: (m) => `U.S. ${m[1]}` },
    { pat: /\b(NASA|GSA|DHS|VA|USDA|USPS|FEMA|EPA|USAID|SSA|HHS|CBP|ICE|TSA|FDA|CDC|NIH|CMS|IRS|DOJ|FBI|DEA|ATF)\b/, out: (m) => m[1] },
  ];
  for (const h of agencyHints) {
    const m = text.match(h.pat);
    if (m) { hints.agency = h.out(m); break; }
  }
  // Response due date: "responses due", "proposals due", "due date", "closing date"
  const dueMatch = text.match(/(?:responses?|proposals?|offers?|quotes?)\s+(?:are\s+)?due(?:\s+by)?[:\s]+([A-Z][a-z]+\s+\d{1,2},?\s+\d{4}|\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4})/i)
                || text.match(/(?:closing\s+date|due\s+date|response\s+date)[:\s]+([A-Z][a-z]+\s+\d{1,2},?\s+\d{4}|\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4})/i);
  if (dueMatch) {
    const parsed = new Date(dueMatch[1]);
    if (!Number.isNaN(parsed.getTime())) {
      hints.response_due_date = parsed.toISOString().slice(0, 10);
    }
  }
  // Ceiling / not-to-exceed amount
  const ceilMatch = text.match(/(?:ceiling|not[-\s]to[-\s]exceed|NTE|estimated\s+value|contract\s+value)[^$0-9]{0,30}(\$?[\d,]+(?:\.\d+)?\s*(?:million|billion|M|B)?)/i);
  if (ceilMatch) hints.estimated_ceiling = ceilMatch[1].trim();
  // Opportunity / title
  const titleMatch = text.match(/(?:project\s+title|title|subject|opportunity)[:\s]+([A-Z][^\n]{5,120})/i);
  if (titleMatch) hints.opportunity_name = titleMatch[1].trim();
  return hints;
}

export function CustomerPursuitNew() {
  useDocMeta({ title: "Start New Pursuit · GovBidIQ", path: "/app/pursuits/new" });
  const nav = useNavigate();
  const { custApi } = useCustomerAuth();
  const fileRef = useRef(null);

  const [step, setStep] = useState(1); // 1=upload, 2=intake, 3=profile
  const [filename, setFilename] = useState("");
  const [text, setText] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [intake, setIntake] = useState({
    opportunity_name: "",
    agency: "",
    solicitation_number: "",
    estimated_ceiling: "",
    response_due_date: "",
    notes: "",
  });
  const [profile, setProfile] = useState({ ...EMPTY_PROFILE });
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Prefetch saved contractor profile so returning customers skip the profile step.
  useEffect(() => {
    custApi.get("/customer/profile")
      .then((r) => setProfile({ ...EMPTY_PROFILE, ...(r.data || {}) }))
      .catch(() => { /* ignore — user will fill it in */ })
      .finally(() => setProfileLoaded(true));
  }, [custApi]);

  const handleFile = useCallback(async (f) => {
    if (!f) return;
    setExtracting(true);
    setFilename(f.name);
    try {
      const data = await extractDocument(f);
      setText(data.text);
      setCharCount(data.characters || (data.text || "").length);
      // Sniff intake hints — customer confirms/corrects in step 2. Only fill
      // fields the customer hasn't already touched (don't clobber their edits).
      const hints = sniffIntakeHints(data.text || "");
      setIntake((prev) => {
        const merged = { ...prev };
        for (const [k, v] of Object.entries(hints)) {
          if (v && !merged[k]) merged[k] = v;
        }
        return merged;
      });
      toast.success(`Extracted ${(data.characters || 0).toLocaleString()} characters`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed");
      setFilename("");
    } finally {
      setExtracting(false);
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const canAdvanceStep1 = text.trim().length >= 200;
  const canAdvanceStep2 = true; // all intake fields optional per exec decision
  const canSubmit = profile.company_name.trim().length > 0;

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Please provide at least your company name.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        solicitation_text: text,
        document_name: filename || intake.opportunity_name || "New pursuit",
        intake: Object.fromEntries(Object.entries(intake).filter(([, v]) => v && String(v).trim() !== "")),
        contractor_profile: profile,
        save_profile: true,
      };
      const res = await custApi.post("/customer/analyses", payload);
      toast.success("Pursuit created — analysis running");
      nav(`/app/pursuits/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create pursuit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="cust-pursuit-new">
      <Link to="/app/pursuits" className="font-mono text-[10px] tracking-widest uppercase text-slate-500 hover:text-sky-400 inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to pursuits
      </Link>
      <div className="tick-label text-sky-400 mb-1">/ New Pursuit · Step {step} of 3</div>
      <h1 className="font-display font-black text-3xl text-slate-100 mb-2">Start a new pursuit</h1>
      <p className="text-slate-400 text-sm mb-6 leading-relaxed">
        Upload your solicitation. We auto-extract everything we can — you confirm what matters and start the analysis.
      </p>

      {/* Stepper */}
      <ol className="flex items-center gap-2 mb-8" data-testid="cust-pursuit-new-stepper">
        {[
          { n: 1, label: "Solicitation" },
          { n: 2, label: "Intake" },
          { n: 3, label: "Company" },
        ].map((it, i, arr) => (
          <li key={it.n} className="flex items-center gap-2 flex-1">
            <div
              data-testid={`cust-pursuit-new-step-${it.n}`}
              className={`flex items-center gap-2 px-3 py-1.5 border ${step === it.n ? "border-sky-500 bg-sky-500/10 text-sky-300" : step > it.n ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-[var(--border-soft)] bg-[var(--bg-secondary)] text-slate-500"}`}
            >
              {step > it.n ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="font-mono text-[10px]">{it.n}</span>}
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase">{it.label}</span>
            </div>
            {i < arr.length - 1 && <div className="h-px flex-1 bg-[var(--border-soft)]" />}
          </li>
        ))}
      </ol>

      {/* Step 1 — Upload */}
      {step === 1 && (
        <section className="space-y-5" data-testid="cust-pursuit-new-upload">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="border border-dashed border-sky-500/40 bg-sky-500/5 p-10 text-center cursor-pointer hover:border-sky-500/70"
            onClick={() => fileRef.current?.click()}
            data-testid="cust-pursuit-new-dropzone"
          >
            <Upload className="w-8 h-8 text-sky-400 mx-auto mb-3" strokeWidth={1.6} />
            <div className="font-display font-bold text-slate-100 text-lg mb-1">Upload the solicitation</div>
            <div className="text-slate-400 text-sm mb-4">PDF, DOCX, or TXT · drop it here or click to browse</div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="cust-pursuit-new-file-input"
            />
            {extracting && (
              <div className="mt-4 inline-flex items-center gap-2 text-sky-300 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Extracting…
              </div>
            )}
            {!extracting && filename && (
              <div className="mt-4 inline-flex items-center gap-2 text-emerald-300 text-sm font-mono" data-testid="cust-pursuit-new-filename">
                <FileText className="w-4 h-4" /> {filename} · {charCount.toLocaleString()} chars
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              disabled={!canAdvanceStep1}
              onClick={() => setStep(2)}
              data-testid="cust-pursuit-new-next-1"
              className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5"
            >
              Next: Confirm intake <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
          </div>
        </section>
      )}

      {/* Step 2 — Intake (minimum manual) */}
      {step === 2 && (
        <section className="space-y-5" data-testid="cust-pursuit-new-intake">
          <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5">
            <div className="tick-label text-sky-400 mb-3">/ Opportunity intake</div>
            <p className="text-xs text-slate-500 mb-4">
              We auto-extracted what we could. Confirm or correct — every field is optional. Missing data will be filled in by the analysis.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <IntakeField label="Opportunity Name"       testid="cust-pursuit-new-opportunity-name"   value={intake.opportunity_name}     onChange={(v) => setIntake({ ...intake, opportunity_name: v })}     placeholder="e.g. DoD Cyber Range Modernization" />
              <IntakeField label="Agency"                  testid="cust-pursuit-new-agency"             value={intake.agency}               onChange={(v) => setIntake({ ...intake, agency: v })}               placeholder="e.g. Department of Defense" />
              <IntakeField label="Solicitation Number"    testid="cust-pursuit-new-solicitation-number" value={intake.solicitation_number}  onChange={(v) => setIntake({ ...intake, solicitation_number: v })}  placeholder="e.g. W91QVN-25-R-0001" mono />
              <IntakeField label="Estimated Ceiling"      testid="cust-pursuit-new-estimated-ceiling"  value={intake.estimated_ceiling}    onChange={(v) => setIntake({ ...intake, estimated_ceiling: v })}    placeholder="e.g. $10M" />
              <IntakeField label="Response Due Date"      testid="cust-pursuit-new-response-due-date"  value={intake.response_due_date}    onChange={(v) => setIntake({ ...intake, response_due_date: v })}    placeholder="YYYY-MM-DD" type="date" />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => setStep(1)}
              data-testid="cust-pursuit-new-back-2"
              className="inline-flex items-center gap-2 border border-[var(--border-soft)] hover:border-sky-500/60 text-slate-300 font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2.5"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <button
              disabled={!canAdvanceStep2}
              onClick={() => setStep(3)}
              data-testid="cust-pursuit-new-next-2"
              className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5"
            >
              Next: Company profile <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
          </div>
        </section>
      )}

      {/* Step 3 — Contractor profile */}
      {step === 3 && (
        <section className="space-y-5" data-testid="cust-pursuit-new-profile">
          {!profileLoaded && (
            <div className="text-slate-500 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading your saved profile…</div>
          )}
          {profileLoaded && (
            <>
              <p className="text-xs text-slate-500">
                Your profile is saved on your account after submission so you can skip this step on future pursuits.
              </p>
              {INTAKE_PROFILE_GROUPS.map((group) => {
                const GroupIcon = group.icon;
                return (
                  <div key={group.label} className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <GroupIcon className="w-3.5 h-3.5 text-sky-400" strokeWidth={1.8} />
                      <div className="tick-label text-sky-400">/ {group.label}</div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {group.fields.map((f) => (
                        <IntakeField
                          key={f.name}
                          label={f.label + (f.required ? " *" : "")}
                          testid={`cust-pursuit-new-profile-${f.name}`}
                          value={profile[f.name] || ""}
                          onChange={(v) => setProfile({ ...profile, [f.name]: v })}
                          placeholder={f.placeholder}
                          type={f.type}
                          fullSpan={f.type === "textarea"}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}

              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => setStep(2)}
                  data-testid="cust-pursuit-new-back-3"
                  className="inline-flex items-center gap-2 border border-[var(--border-soft)] hover:border-sky-500/60 text-slate-300 font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2.5"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <button
                  disabled={!canSubmit || submitting}
                  onClick={submit}
                  data-testid="cust-pursuit-new-submit"
                  className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-5 py-2.5"
                >
                  {submitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating pursuit…</> : <><Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} /> Create pursuit &amp; analyze</>}
                </button>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}

function IntakeField({ label, value, onChange, placeholder, type, mono, testid, fullSpan }) {
  const isTextarea = type === "textarea";
  const Cmp = isTextarea ? "textarea" : "input";
  return (
    <label className={`block ${isTextarea || fullSpan ? "sm:col-span-2" : ""}`}>
      <span className="tick-label mb-1.5 inline-block">{label}</span>
      <Cmp
        type={isTextarea ? undefined : (type || "text")}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        rows={isTextarea ? 3 : undefined}
        className={`w-full bg-[var(--bg-primary)] border border-[var(--border-soft)] focus:border-sky-500/70 outline-none text-slate-100 text-sm px-3 py-2 ${mono ? "font-mono" : ""}`}
      />
    </label>
  );
}

// -------- Pursuit Detail (identical body to prior Report Detail; renamed for framing) --------
export function CustomerPursuitDetail() {
  const { id } = useParams();
  const { custApi } = useCustomerAuth();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  useDocMeta({ title: "Pursuit · GovBidIQ", path: `/app/pursuits/${id}` });

  // R0.1.x — Fetch once, then poll every 15s while status === "processing".
  // Stops on "complete" or "failed". Cleaned up on unmount / id change.
  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    const fetchOnce = () =>
      custApi
        .get(`/customer/reports/${id}`)
        .then((r) => {
          if (cancelled) return;
          setDoc(r.data);
          const status = (r.data && r.data.status) || "";
          // Any terminal status ends polling. Anything else keeps polling.
          if (status === "complete" || status === "failed") {
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          }
        })
        .catch((e) => {
          if (cancelled) return;
          setErr(e?.response?.data?.detail || "Not found");
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        });

    fetchOnce();
    intervalId = setInterval(fetchOnce, 15000);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [custApi, id]);

  if (err) return (
    <div data-testid="cust-pursuit-error">
      <div className="border border-rose-500/40 bg-rose-500/5 p-4 inline-flex items-start gap-2 mb-4"><AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5" /><div className="text-sm text-slate-300">{err}</div></div>
      <Link to="/app/pursuits" className="block font-mono text-[10px] tracking-widest uppercase text-sky-400">← Back to pursuits</Link>
    </div>
  );
  if (!doc) return <Loader2 className="w-4 h-4 animate-spin text-slate-500" />;

  // R0.1.x — Explicit processing state. Never surface fake — /100 scores or
  // INSUFFICIENT DATA to the customer while the LLM pipeline is running.
  const docStatus = (doc.status || "").toLowerCase();
  if (docStatus === "processing" || docStatus === "queued" || docStatus === "pending" || (!doc.report && docStatus !== "failed" && docStatus !== "complete")) {
    const es0 = (doc.report && doc.report.executive_summary) || {};
    const intake0 = doc.intake || {};
    const displayName =
      es0.opportunity_name ||
      intake0.opportunity_name ||
      doc.document_name ||
      doc.id;
    return (
      <div className="space-y-6 max-w-[1100px]" data-testid="cust-pursuit-processing">
        <Link to="/app/pursuits" className="font-mono text-[10px] tracking-widest uppercase text-slate-500 hover:text-sky-400 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to pursuits</Link>
        <header>
          <div className="tick-label text-sky-400 mb-1">/ Pursuit</div>
          <h1 className="font-display font-black text-3xl text-slate-100" data-testid="cust-pursuit-processing-name">{displayName}</h1>
        </header>
        <div className="border border-sky-500/40 bg-sky-500/5 p-8 flex flex-col items-center text-center space-y-4" data-testid="cust-pursuit-processing-card">
          <Loader2 className="w-6 h-6 animate-spin text-sky-400" strokeWidth={1.4} />
          <div className="tick-label text-sky-400" data-testid="cust-pursuit-processing-label">Analysis in progress</div>
          <p className="text-slate-200 text-sm leading-relaxed max-w-[560px]">
            GovBidIQ is analyzing the solicitation against your company profile.
            Comprehensive analyses can take several minutes. You can keep this
            page open while we finish.
          </p>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">Checking every 15 seconds…</div>
        </div>
      </div>
    );
  }

  // R0.1.x — Explicit failure state. Never render an empty-report skeleton
  // for a genuinely failed analysis. Do NOT expose the raw exception
  // string to customers — it may contain sensitive diagnostic info.
  if (docStatus === "failed") {
    const es0 = (doc.report && doc.report.executive_summary) || {};
    const intake0 = doc.intake || {};
    const displayName =
      es0.opportunity_name ||
      intake0.opportunity_name ||
      doc.document_name ||
      doc.id;
    return (
      <div className="space-y-6 max-w-[1100px]" data-testid="cust-pursuit-failed">
        <Link to="/app/pursuits" className="font-mono text-[10px] tracking-widest uppercase text-slate-500 hover:text-sky-400 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to pursuits</Link>
        <header>
          <div className="tick-label text-rose-400 mb-1">/ Pursuit</div>
          <h1 className="font-display font-black text-3xl text-slate-100" data-testid="cust-pursuit-failed-name">{displayName}</h1>
        </header>
        <div className="border border-rose-500/40 bg-rose-500/5 p-8 space-y-4" data-testid="cust-pursuit-failed-card">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-400 mt-0.5 flex-shrink-0" strokeWidth={1.4} />
            <div>
              <div className="tick-label text-rose-400 mb-2" data-testid="cust-pursuit-failed-label">Analysis could not be completed</div>
              <p className="text-slate-200 text-sm leading-relaxed">
                We encountered a problem while analyzing this pursuit. Your
                solicitation and company information are still saved and can
                be reviewed by our team.
              </p>
            </div>
          </div>
          <div className="pt-2">
            <a
              href={`mailto:BidNoBidIQ@aol.com?subject=Analysis%20support%20request%20(pursuit%20${encodeURIComponent(doc.id)})`}
              data-testid="cust-pursuit-failed-support"
              className="inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[10px] tracking-widest uppercase px-4 py-2"
            >
              Contact GovBidIQ support
            </a>
          </div>
        </div>
      </div>
    );
  }

  const report = doc.report || {};
  const es = report.executive_summary || {};
  const final = report.final_recommendation || {};
  const pursuit = report.executive_pursuit || {};
  const win = report.win_probability || {};
  const sc = report.scorecard || {};
  const hasExecMeta = Boolean(es.agency || es.solicitation_number || es.contract_type ||
    es.contract_vehicle || es.set_aside_status || es.period_of_performance);
  const hasScorecard = Boolean(sc && Object.keys(sc).some((k) => k !== "overall" && typeof sc[k] === "number"));
  // R0.1.x — Wrap in Boolean() to prevent React from rendering the literal
  // number 0 when all sub-values are empty/undefined and the last .length
  // returns 0. Same audit applied to hasExecMeta/hasScorecard above.
  const hasFinal = Boolean(final.verdict || final.why ||
    (final.key_strengths || []).length || (final.key_concerns || []).length ||
    (final.immediate_next_actions || []).length);

  return (
    <div className="space-y-6 max-w-[1100px]" data-testid="cust-pursuit-detail">
      <Link to="/app/pursuits" className="font-mono text-[10px] tracking-widest uppercase text-slate-500 hover:text-sky-400 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to pursuits</Link>

      <header>
        <div className="tick-label text-sky-400 mb-1">/ Pursuit</div>
        <h1 className="font-display font-black text-3xl text-slate-100">{es.opportunity_name || doc.document_name || doc.id}</h1>
        <div className="text-slate-400 text-sm mt-1 flex items-center gap-3 flex-wrap">
          <span>{[es.agency, es.solicitation_number, (doc.contractor_profile || {}).company_name].filter(Boolean).join("  ·  ")}</span>
          {doc.tier && (
            <span data-testid="cust-pursuit-tier-badge" className="inline-block bg-sky-500/10 border border-sky-500/40 text-sky-300 px-2 py-0.5 uppercase tracking-widest text-[9px] font-mono">
              Tier · {doc.tier}
            </span>
          )}
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[var(--border-soft)]" data-testid="cust-pursuit-hero">
        <ScoreCell label="Opportunity Score" value={report.opportunity_score} accent="sky" testid="cust-score-opportunity" />
        <ScoreCell label="Contractor Match" value={report.contractor_match_score} accent="sky" testid="cust-score-match" />
        <ScoreCell label="Pursuit ROI" value={report.pursuit_roi_score} accent="amber" testid="cust-score-pursuit" />
        <div className="bg-[var(--bg-secondary)] p-4" data-testid="cust-pursuit-win-cell">
          <div className="tick-label mb-2">Win Probability</div>
          <div className="mb-2"><RiskBadge value={win.level} /></div>
          {win.rationale && <div className="text-xs text-slate-400 leading-relaxed line-clamp-3">{win.rationale}</div>}
        </div>
        <div className="bg-[var(--bg-secondary)] p-4" data-testid="cust-pursuit-verdict-cell">
          <div className="tick-label mb-2">Bid Recommendation</div>
          {(() => {
            const rec = deriveRecommendation({ final_verdict: final.verdict });
            const recS = recommendationStyle(rec);
            return (
              <div
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 border ${recS.border} ${recS.bg} mb-2`}
                data-testid="cust-pursuit-verdict-canonical"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${recS.dot}`} />
                <span className={`font-mono text-[10px] tracking-[0.2em] uppercase font-bold ${recS.text}`}>{rec}</span>
              </div>
            );
          })()}
          {(() => {
            const conf = analysisConfidence({ analysis_confidence: final.analysis_confidence });
            if (conf === null) return null;
            const cS = confidenceStyle(conf);
            return (
              <div
                className={`inline-flex items-center gap-1 px-2 py-0.5 border ${cS.border} ${cS.bg}`}
                data-testid="cust-pursuit-analysis-confidence"
                title="Analysis Confidence — confidence in the analysis based on available information. Not a probability of contract award."
              >
                <span className={`font-mono text-[9px] tracking-[0.15em] uppercase ${cS.text}`}>Analysis Confidence</span>
                <span className={`font-mono text-[11px] font-bold ${cS.text}`}>{conf}%</span>
              </div>
            );
          })()}
          {pursuit.recommendation && (
            <div className="font-mono text-[10px] text-slate-500 mt-2 truncate" title={pursuit.recommendation} data-testid="cust-pursuit-recommendation">
              Pursuit: <span className="text-slate-300">{pursuit.recommendation}</span>
            </div>
          )}
        </div>
      </div>

      {/* Analysis Confidence disambiguation footnote (Story #3 requirement) */}
      {typeof final.analysis_confidence === "number" && (
        <p className="font-mono text-[10px] tracking-widest uppercase text-slate-500" data-testid="cust-pursuit-confidence-note">
          Analysis Confidence reflects confidence in the analysis based on available information — not a probability of contract award.
        </p>
      )}

      {/* Executive Brief CTA (Story #4.5) + Request Services (Story #5) */}
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to={`/app/pursuits/${doc.id}/brief`}
          data-testid="cust-pursuit-brief-cta"
          className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5"
        >
          <FileText className="w-3.5 h-3.5" strokeWidth={2.5} /> Executive Opportunity Brief
        </Link>
        <button
          onClick={() => setServiceModalOpen(true)}
          data-testid="cust-pursuit-request-services"
          className="inline-flex items-center gap-2 border border-sky-500/60 text-sky-300 hover:bg-sky-500/10 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5"
        >
          <Briefcase className="w-3.5 h-3.5" /> Request Proposal Services
        </button>
        <Link to={`/app/copilot?id=${doc.id}`} data-testid="cust-pursuit-copilot-cta" className="inline-flex items-center gap-2 border border-sky-500/60 text-sky-300 hover:bg-sky-500/10 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
          <MessageCircle className="w-3.5 h-3.5" /> Ask Copilot
        </Link>
      </div>
      <RequestProposalServicesModal
        open={serviceModalOpen}
        onClose={() => setServiceModalOpen(false)}
        pursuitId={doc.id}
        custApi={custApi}
        opportunityName={es.opportunity_name || doc.document_name || "This pursuit"}
      />

      {report.pursuit_roi_rationale && (
        <div className="border-l-2 border-amber-500/60 bg-[var(--bg-secondary)] p-4" data-testid="cust-pursuit-roi-rationale">
          <div className="tick-label text-amber-400 mb-1">/ Pursuit ROI Rationale</div>
          <p className="text-sm text-slate-200 leading-relaxed">{report.pursuit_roi_rationale}</p>
        </div>
      )}

      {(es.overall_assessment || hasExecMeta) && (
        <section className="space-y-3" data-testid="cust-pursuit-exec-summary">
          <div className="tick-label text-sky-400">/ Executive Summary</div>
          {es.overall_assessment && (
            <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5">
              <p className="text-slate-200 leading-relaxed">{es.overall_assessment}</p>
            </div>
          )}
          {hasExecMeta && (
            <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5">
              <KV label="Agency" value={es.agency} />
              <KV label="Solicitation #" value={es.solicitation_number} mono />
              <KV label="Opportunity" value={es.opportunity_name} />
              <KV label="Contract Type" value={es.contract_type} />
              <KV label="Contract Vehicle" value={es.contract_vehicle} />
              <KV label="Set-Aside" value={es.set_aside_status} />
              <KV label="Period of Performance" value={es.period_of_performance} />
            </div>
          )}
        </section>
      )}

      {/* Story #4 — Compliance Snapshot Card (executive-first, detail collapsed) */}
      <ComplianceSnapshot report={report} />

      {hasScorecard && (
        <section className="space-y-3" data-testid="cust-pursuit-scorecard">
          <div className="tick-label text-sky-400">/ GovBidIQ Scorecard</div>
          <Scorecard scorecard={sc} />
        </section>
      )}

      {(win.level || win.rationale) && (
        <section className="space-y-3" data-testid="cust-pursuit-win">
          <div className="tick-label text-sky-400">/ Win Probability</div>
          <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5">
            <div className="flex items-center gap-4 mb-3">
              <div className="tick-label">Probability</div>
              <RiskBadge value={win.level} />
            </div>
            {win.rationale && <p className="text-slate-200 text-sm leading-relaxed">{win.rationale}</p>}
          </div>
        </section>
      )}

      {(pursuit.recommendation || pursuit.reasoning) && (
        <section className="space-y-3" data-testid="cust-pursuit-recommendation-section">
          <div className="tick-label text-sky-400">/ Executive Pursuit Recommendation</div>
          <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5 flex flex-col sm:flex-row gap-6 items-start">
            <div className="flex-shrink-0">
              <Target className="w-8 h-8 text-sky-400 mb-2" strokeWidth={1.4} />
              <RiskBadge value={pursuit.recommendation} />
            </div>
            {pursuit.reasoning && <p className="text-slate-200 text-sm leading-relaxed flex-1">{pursuit.reasoning}</p>}
          </div>
        </section>
      )}

      {hasFinal && (
        <section className="space-y-3" data-testid="cust-pursuit-final">
          <div className="tick-label text-sky-400">/ Final Recommendation</div>
          <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5">
            <div className="flex items-center gap-4 mb-3">
              <div className="tick-label">Verdict</div>
              <RiskBadge value={final.verdict} />
            </div>
            {final.why && <p className="text-slate-200 text-sm leading-relaxed mb-5">{final.why}</p>}
            <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)]">
              <div className="bg-[var(--bg-primary)] p-4" data-testid="cust-pursuit-strengths">
                <div className="tick-label text-emerald-400 mb-2">Key Strengths</div>
                <Bullets items={final.key_strengths} />
              </div>
              <div className="bg-[var(--bg-primary)] p-4" data-testid="cust-pursuit-concerns">
                <div className="tick-label text-rose-400 mb-2">Key Concerns</div>
                <Bullets items={final.key_concerns} />
              </div>
              <div className="bg-[var(--bg-primary)] p-4" data-testid="cust-pursuit-next-actions">
                <div className="tick-label text-sky-400 mb-2">Immediate Next Actions</div>
                <Bullets items={final.immediate_next_actions} />
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

const ScoreCell = ({ label, value, accent, testid }) => {
  const color = accent === "amber" ? "text-amber-400" : accent === "emerald" ? "text-emerald-400" : accent === "rose" ? "text-rose-400" : "text-sky-400";
  return (
    <div className="bg-[var(--bg-secondary)] p-4" data-testid={testid}>
      <div className="tick-label mb-2">{label}</div>
      <div className="flex items-baseline gap-1.5">
        <span className={`metric-number text-3xl leading-none ${color}`}>{value ?? "—"}</span>
        <span className="font-mono text-xs text-slate-500">/100</span>
      </div>
    </div>
  );
};

// -------- Story #4 — Compliance Snapshot Card --------
// Executive-first: Overall Compliance Status · Top Risks · Key Evaluation
// Factors · Recommended Next Actions. The detailed FAR/DFARS analysis stays
// collapsed by default so the executive summary doesn't get buried.
function complianceStatusFromReport(report) {
  const cr = report?.compliance_review || {};
  const far = Array.isArray(report?.far_dfars_analysis)
    ? report.far_dfars_analysis
    : (report?.far_dfars_analysis?.clauses || []);
  const disq = (cr.disqualification_risks || []).length;
  const highs = far.filter((c) => (c?.risk_level || "").toLowerCase() === "high").length;
  const mods = far.filter((c) => (c?.risk_level || "").toLowerCase() === "moderate").length;
  const cyber = (cr.cybersecurity_requirements || []).length;
  const security = (cr.security_requirements || []).length;
  if (disq >= 1 || highs >= 2) return { label: "High Risk", tone: "rose", detail: "Multiple hard compliance risks require resolution before proceeding." };
  if (highs === 1 || mods >= 3 || cyber >= 3 || security >= 3) return { label: "Elevated Risk", tone: "amber", detail: "Manageable but material compliance obligations to plan around." };
  if (mods >= 1 || (far.length >= 3)) return { label: "Moderate", tone: "sky", detail: "Standard federal compliance footprint — no critical blockers surfaced." };
  return { label: "Minimal Risk", tone: "emerald", detail: "Compliance surface is light for this opportunity." };
}

function toneChip(tone) {
  return {
    rose:    { text: "text-rose-300",    border: "border-rose-500/60", bg: "bg-rose-500/10",    dot: "bg-rose-400" },
    amber:   { text: "text-amber-300",   border: "border-amber-500/60", bg: "bg-amber-500/10",   dot: "bg-amber-400" },
    sky:     { text: "text-sky-300",     border: "border-sky-500/60",  bg: "bg-sky-500/10",     dot: "bg-sky-400" },
    emerald: { text: "text-emerald-300", border: "border-emerald-500/60", bg: "bg-emerald-500/10", dot: "bg-emerald-400" },
    slate:   { text: "text-slate-300",   border: "border-slate-500/40", bg: "bg-slate-500/10",   dot: "bg-slate-400" },
  }[tone] || { text: "text-slate-300", border: "border-slate-500/40", bg: "bg-slate-500/10", dot: "bg-slate-400" };
}

export function ComplianceSnapshot({ report }) {
  const [showDetail, setShowDetail] = useState(false);
  if (!report) return null;
  const cr = report.compliance_review || {};
  const far = Array.isArray(report.far_dfars_analysis)
    ? report.far_dfars_analysis
    : (report.far_dfars_analysis?.clauses || []);
  const risks = (report.opportunity_risks || []).slice(0, 5);
  const evalC = report.evaluation_criteria || {};
  const keyFactors = (
    (evalC.most_important_factors && evalC.most_important_factors.length ? evalC.most_important_factors : null) ||
    evalC.likely_discriminators || evalC.technical_factors || []
  ).slice(0, 5);
  const actions = ((report.final_recommendation && report.final_recommendation.immediate_next_actions) || [])
    .concat((report.top_actions || []).map((a) => (typeof a === "string" ? a : a.action)))
    .filter(Boolean)
    .slice(0, 5);

  const hasSignal = far.length || risks.length || keyFactors.length || actions.length || Object.keys(cr).length;
  if (!hasSignal) return null;

  const status = complianceStatusFromReport(report);
  const s = toneChip(status.tone);

  return (
    <section className="space-y-3" data-testid="cust-pursuit-compliance-snapshot">
      <div className="tick-label text-sky-400">/ Compliance Snapshot</div>
      <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5 space-y-6">
        <div className="flex items-start gap-4 flex-wrap" data-testid="cust-compliance-status">
          <div className="flex-shrink-0">
            <div className="tick-label mb-2">Overall Compliance Status</div>
            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 border ${s.border} ${s.bg}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
              <span className={`font-mono text-[11px] tracking-[0.2em] uppercase font-bold ${s.text}`} data-testid="cust-compliance-status-label">{status.label}</span>
            </div>
          </div>
          <p className="text-sm text-slate-300 leading-relaxed flex-1 min-w-[220px]">{status.detail}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)]">
          <div className="bg-[var(--bg-primary)] p-4" data-testid="cust-compliance-top-risks">
            <div className="tick-label text-rose-400 mb-2">Top Risks</div>
            {risks.length > 0 ? (
              <ul className="space-y-2">
                {risks.map((r, i) => (
                  <li key={i} className="text-xs text-slate-200 leading-relaxed">
                    <div className="flex items-start gap-1.5">
                      <AlertTriangle className="w-3 h-3 text-rose-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-slate-100">{r.type || r.description}</span>
                        {r.description && r.type && <span className="text-slate-400"> — {r.description}</span>}
                        {(r.likelihood || r.impact) && (
                          <span className="font-mono text-[9px] tracking-widest uppercase text-slate-500 block mt-0.5">
                            {r.likelihood ? `Likelihood: ${r.likelihood}` : ""}{r.likelihood && r.impact ? " · " : ""}{r.impact ? `Impact: ${r.impact}` : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-slate-500 italic">No material risks surfaced.</div>
            )}
          </div>

          <div className="bg-[var(--bg-primary)] p-4" data-testid="cust-compliance-eval-factors">
            <div className="tick-label text-sky-400 mb-2">Key Evaluation Factors</div>
            {keyFactors.length > 0 ? (
              <Bullets items={keyFactors} />
            ) : (
              <div className="text-xs text-slate-500 italic">Not specified in the solicitation.</div>
            )}
          </div>

          <div className="bg-[var(--bg-primary)] p-4" data-testid="cust-compliance-next-actions">
            <div className="tick-label text-emerald-400 mb-2">Recommended Next Actions</div>
            {actions.length > 0 ? (
              <Bullets items={actions} />
            ) : (
              <div className="text-xs text-slate-500 italic">No specific actions recommended.</div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowDetail((v) => !v)}
          data-testid="cust-compliance-detail-toggle"
          className="font-mono text-[10px] tracking-[0.2em] uppercase text-sky-400 hover:text-sky-300 inline-flex items-center gap-1.5"
        >
          {showDetail ? "Hide detailed FAR/DFARS analysis" : "Show detailed FAR/DFARS analysis"}
        </button>

        {showDetail && (
          <div className="space-y-4" data-testid="cust-compliance-detail">
            {far.length > 0 && (
              <div>
                <div className="tick-label text-sky-400 mb-2">FAR / DFARS Analysis</div>
                <div className="border border-[var(--border-soft)] divide-y divide-[var(--border-soft)]">
                  {far.map((c, i) => {
                    const risk = (c?.risk_level || "").toLowerCase();
                    const rTone = risk === "high" ? "rose" : risk === "moderate" ? "amber" : "emerald";
                    const rS = toneChip(rTone);
                    return (
                      <div key={i} className="p-3 grid md:grid-cols-4 gap-3 text-xs">
                        <div>
                          <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Clause</div>
                          <div className="font-mono text-slate-100">{c.number || "—"}</div>
                          <div className="text-slate-400">{c.title}</div>
                        </div>
                        <div className="md:col-span-2 text-slate-300 leading-relaxed">
                          {c.purpose && <div className="mb-1"><span className="tick-label">Purpose</span> <span className="text-slate-200">{c.purpose}</span></div>}
                          {c.contractor_impact && <div><span className="tick-label">Impact</span> <span className="text-slate-200">{c.contractor_impact}</span></div>}
                        </div>
                        <div>
                          <div className={`inline-flex items-center gap-1 px-2 py-0.5 border ${rS.border} ${rS.bg}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${rS.dot}`} />
                            <span className={`font-mono text-[9px] tracking-widest uppercase ${rS.text}`}>{c.risk_level || "—"}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {Object.keys(cr).length > 0 && (
              <div>
                <div className="tick-label text-sky-400 mb-2">Compliance Review</div>
                <div className="border border-[var(--border-soft)] bg-[var(--bg-primary)] p-4 grid sm:grid-cols-2 gap-4">
                  {Object.entries(cr).map(([k, v]) => {
                    const items = Array.isArray(v) ? v : (v ? [String(v)] : []);
                    if (items.length === 0) return null;
                    return (
                      <div key={k}>
                        <div className="tick-label text-slate-400 mb-1">{k.replace(/_/g, " ")}</div>
                        <Bullets items={items} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}


// -------- Back-compat aliases (Story #1 requirement: alias /app/reports → pursuits UX) --------
export const CustomerReports = CustomerPursuits;
export const CustomerReportDetail = CustomerPursuitDetail;
